import React from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, Clock, Gift, Lightbulb, AlertTriangle, ChevronRight } from 'lucide-react'
import Navigation from '../../components/common/Navigation'
import Footer from '../../components/common/Footer'
import { getArticle, type Block } from '../../features/tech-hub/techArticles'

const RenderBlock: React.FC<{ b: Block }> = ({ b }) => {
  switch (b.t) {
    case 'h2':
      return <h2 className="mt-8 mb-3 text-xl sm:text-2xl font-black text-slate-900">{b.c}</h2>
    case 'p':
      return <p className="mb-4 text-base sm:text-lg leading-relaxed text-slate-700">{b.c}</p>
    case 'list':
      return (
        <ul className="mb-4 space-y-2">
          {b.items.map((x, i) => (
            <li key={i} className="flex gap-2 text-base sm:text-lg text-slate-700">
              <ChevronRight size={18} className="mt-1 shrink-0 text-blue-500" /> <span>{x}</span>
            </li>
          ))}
        </ul>
      )
    case 'steps':
      return (
        <ol className="mb-4 space-y-3">
          {b.items.map((s, i) => (
            <li key={i} className="flex gap-3 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-blue-600 text-sm font-black text-white">{i + 1}</span>
              <div>
                <p className="font-black text-slate-900">{s.title}</p>
                <p className="mt-0.5 text-base leading-relaxed text-slate-600">{s.desc}</p>
              </div>
            </li>
          ))}
        </ol>
      )
    case 'callout': {
      const warn = b.kind === 'warn'
      return (
        <div className={`mb-4 flex gap-3 rounded-2xl border p-4 ${warn ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-emerald-200 bg-emerald-50 text-emerald-900'}`}>
          {warn ? <AlertTriangle size={20} className="mt-0.5 shrink-0" /> : <Lightbulb size={20} className="mt-0.5 shrink-0" />}
          <p className="text-base leading-relaxed">{b.c}</p>
        </div>
      )
    }
    case 'code':
      return <pre className="mb-4 overflow-x-auto whitespace-pre-wrap rounded-2xl border border-slate-200 bg-slate-900 p-4 text-sm sm:text-base leading-relaxed text-slate-100">{b.c}</pre>
    default:
      return null
  }
}

const TechArticlePage: React.FC = () => {
  const { slug } = useParams()
  const article = getArticle(slug)

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navigation />
      <main className="flex-1">
        {!article ? (
          <div className="mx-auto max-w-3xl px-4 py-20 text-center">
            <p className="text-lg font-black text-slate-800">Không tìm thấy bài viết</p>
            <Link to="/cong-nghe-so" className="mt-3 inline-block font-bold text-blue-600 hover:underline">← Về Công nghệ số cho giáo viên</Link>
          </div>
        ) : (
          <article className="mx-auto max-w-3xl px-4 py-8 sm:py-12">
            <Link to="/cong-nghe-so" className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-500 hover:text-slate-800">
              <ArrowLeft size={16} /> Công nghệ số cho giáo viên
            </Link>
            <div className="mt-4 flex items-center gap-2 text-xs font-black uppercase tracking-wide text-blue-600">
              <span>{article.category}</span>
              <span className="text-slate-300">·</span>
              <span className="inline-flex items-center gap-1 text-slate-400"><Clock size={13} /> {article.readMin} phút đọc</span>
            </div>
            <h1 className="mt-2 text-2xl sm:text-3xl font-black leading-tight text-slate-900">{article.emoji} {article.title}</h1>
            <p className="mt-3 text-base sm:text-lg leading-relaxed text-slate-500">{article.excerpt}</p>

            <div className="mt-8">
              {article.blocks.map((b, i) => <RenderBlock key={i} b={b} />)}
            </div>

            {article.giftSlug && (
              <div className="mt-10 rounded-3xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-blue-50 p-6">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-emerald-600 text-white"><Gift size={24} /></span>
                    <div>
                      <p className="font-black text-slate-900">Tài liệu kèm bài viết</p>
                      <p className="text-sm text-slate-600">{article.giftLabel || 'Tải tài liệu miễn phí'} — đăng nhập bằng Google để nhận.</p>
                    </div>
                  </div>
                  <Link
                    to={`/qua/${article.giftSlug}`}
                    className="shrink-0 inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 font-black text-white hover:bg-emerald-700 no-underline"
                  >
                    <Gift size={18} /> Nhận tài liệu
                  </Link>
                </div>
              </div>
            )}
          </article>
        )}
      </main>
      <Footer />
    </div>
  )
}

export default TechArticlePage
